/**
 * №16 S4 — resolve a step assignee to a human display name + avatar initials.
 *
 * The projection carries the raw `assigneeCode`: an AGENT is an `ln_…` node id, a
 * ROLE is a key ("assigned"), a PERSON is a user id. Rendering that raw code gave
 * an agent step the avatar "LN" (the node-id prefix) and "prepared by ln_8f42…".
 * This maps the code to a readable name — agents via the roster directory
 * (`list_invocable_agents`), roles via a friendly label — with proper initials.
 */
const ROLE_LABELS: Record<string, string> = {
  manager: "Manager",
  assigned: "Assigned lawyer",
  assisting: "Assisting lawyer",
  originating: "Originating lawyer",
  person: "A person",
};

function initialsOf(s: string | undefined, agent: boolean): string {
  if (!s) return agent ? "AG" : "?";
  const words = s.trim().split(/\s+/).filter(Boolean);
  const base = words.length >= 2 ? words[0]!.charAt(0) + words[1]!.charAt(0) : s.replace(/[^A-Za-z0-9]/g, "").slice(0, 2);
  return (base || (agent ? "AG" : "?")).toUpperCase();
}

export interface AssigneeDisplay { who: string; initials: string; isAgent: boolean; }

export function assigneeDisplay(
  kind: string | undefined,
  code: string | undefined,
  agentNames?: Record<string, string>,
): AssigneeDisplay {
  if (kind === "agent") {
    const name = (code && agentNames?.[code]) || code || "an agent";
    return { who: name, initials: initialsOf(name, true), isAgent: true };
  }
  if (kind === "role") {
    const label = (code && ROLE_LABELS[code]) || (code ? code.charAt(0).toUpperCase() + code.slice(1) : "the team");
    return { who: label, initials: initialsOf(label, false), isAgent: false };
  }
  if (kind === "person") {
    return { who: code || "a person", initials: initialsOf(code, false), isAgent: false };
  }
  return { who: code || "the team", initials: initialsOf(code || "team", false), isAgent: false };
}
